/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, OnInit, Input, ViewEncapsulation } from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  Validators,
  UntypedFormControl,
} from '@angular/forms';
import {
  ReportsSetupFormService,
  ReportFormData,
} from '../reports-setup-form-service';
import { AuthService } from 'src/app/service/auth.service';
import { Observable, of } from 'rxjs';
import { map, debounceTime, mergeMap } from 'rxjs/operators';
import { YukkApi } from 'src/app/service/yukkapi.service';
import { ENTER } from '@angular/cdk/keycodes';
import { MatLegacyChipInputEvent as MatChipInputEvent } from '@angular/material/legacy-chips';
import { ReportsService } from '../reports-service';
import { SettingsUtilsService } from '../../settings-utils.service';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-reports-form',
  templateUrl: './reports-form.component.html',
  styleUrls: ['./reports-form.component.scss'],
})
export class ReportsFormComponent implements OnInit {
  @Input() scoreOptions: any[] = [];

  reportForm: FormGroup;
  currentReportType: string = 'entity';
  isNewReport = true;
  searchFormControl = new UntypedFormControl();
  folio$: Observable<any>;
  searchItems$: Observable<any>;
  loadingSearch = false;
  scoreTypes: any[] = [];
  schedules: { value: string; label: string }[] = [
    { value: 'every-monday', label: 'Every Monday' },
    { value: 'tuesday-at-noon', label: 'Tuesday at noon' },
    { value: 'every-month', label: 'Every month' },
  ];
  domain: string;
  user: any;
  emailList: string[] = [];
  readonly separatorKeysCodes: number[] = [ENTER];

  constructor(
    private fb: FormBuilder,
    private reportFormService: ReportsSetupFormService,
    private reportService: ReportsService,
    private auth: AuthService,
    private yukkApi: YukkApi,
    private settingsUtils: SettingsUtilsService,
  ) {
    this.folio$ = this.auth.portFolios();
    this.domain = auth.authToken().email.split('@')[1];
    this.user = auth.authToken();
  }

  ngOnInit(): void {
    this.initilazeScoreTypes();
    this.reportFormService.currentReport$.subscribe((report) => {
      this.updateForm(report);
      this.currentReportType = report.reportType || 'entity';
      this.isNewReport = report.isNew;
    });
  }

  initForm(): void {
    this.reportForm = this.fb.group({
      uid: [null],
      reportType: ['entity', Validators.required],
      active: [true],
      reportObject: [null, Validators.required],
      schedule: [null, Validators.required],
      score: ['', Validators.required],
      recipients: [[]],
      isNew: [true],
    });
  }

  updateForm(report: ReportFormData): void {
    if (!this.reportForm) {
      this.initForm();
    }
    if (report.reportType === 'entity') {
      this.searchFormControl.setValue(report.entityName || '');
    }

    this.reportForm.patchValue({
      uid: report.uid,
      reportType: report.reportType,
      active: report.active,
      reportObject: report.reportObject,
      schedule: report.schedule,
      score: report.score,
      recipients: report.recipients,
      isNew: report.isNew,
    });

    this.emailList = report.recipients || [];
    this.reportForm.get('recipients')?.setValue(this.emailList);

    this.searchFormControl.valueChanges
      .pipe(debounceTime(1000))
      .subscribe((value) => {
        if (value && value.length > 1) {
          this.loadingSearch = true;
          this.searchItems$ = this.yukkApi.search(value, false).pipe(
            mergeMap((result) => {
              if (result && result.hits.length === 0) {
                const words = value.split(' ').filter((item) => item !== '');
                return this.yukkApi
                  .search(words[0] ? words[0] : ' ', false)
                  .pipe(
                    map((result2) => {
                      this.loadingSearch = false;
                      result2.hits = result2.hits.filter(
                        (hit) => hit.entity.type === 'company',
                      );
                      return result2;
                    }),
                  );
              } else {
                this.loadingSearch = false;
                result.hits = result.hits.filter(
                  (hit) => hit.entity.type === 'company',
                );
                return of(result);
              }
            }),
          );
        }
      });
  }

  saveReport(): void {
    if (this.reportForm.valid) {
      const formData: ReportFormData = {
        ...this.reportForm.value,
        recipients: this.emailList,
      };
      const subscription =
        this.reportFormService.convertFormToSubscription(formData);

      this.reportService.createOrUpdateSubscription(subscription).subscribe({
        next: () => {
          this.reportFormService.resetForm();
        },
      });
    } else {
      this.markFormGroupTouched(this.reportForm);
      console.warn('Please fill out all required fields correctly.');
    }
  }

  onOptionSelected(option: any): void {
    this.searchFormControl.setValue(option.entity.name);
    this.reportForm.patchValue({
      reportObject:
        option.entity.compound_key ||
        `${option.entitiy.type}:${option.entity.id} `,
      reportType: 'entity',
    });
  }

  deleteReport(): void {
    const uid = this.reportForm.get('uid')?.value;
    this.reportService.deleteSubscription(uid).subscribe(() => {
      this.reportFormService.resetForm();
    });
  }

  /**
   * format big numbers >999 into K, M, ...
   */
  numberFormat(num: number) {
    return this.settingsUtils.numberFormat(num);
  }

  iMatch(entity) {
    return this.settingsUtils.iMatch(entity);
  }

  removeEmail(email: string): void {
    this.emailList = this.settingsUtils.removeEmail(this.emailList, email);
    this.reportForm.get('recipients')?.setValue(this.emailList);
  }

  addEmail(event: MatChipInputEvent): void {
    const input = event.input;
    const value = event.value;

    if ((value || '').trim()) {
      const emails = this.settingsUtils.extractEmails(value.trim());
      if (emails) {
        const { updatedList, error } = this.settingsUtils.addEmails(
          this.emailList,
          emails,
          this.user.email,
          this.domain,
        );

        if (error) {
          alert(error);
        } else {
          this.emailList = updatedList;
          this.reportForm.get('recipients')?.setValue(this.emailList);
        }
      } else {
        alert('Error: please enter valid email.');
      }
    }

    if (input) {
      input.value = '';
    }
  }

  extractEmails(text) {
    return this.settingsUtils.extractEmails(text);
  }
  reportTypeSelect() {
    this.currentReportType = this.reportForm.get('reportType')?.value;
    this.searchFormControl.setValue('');
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }
  private initilazeScoreTypes() {
    this.scoreTypes = this.scoreOptions.map((item) => ({
      value: item.value,
      label: item.label,
    }));
  }
}
